import { useMemo, useState } from 'react';
import { Select } from 'antd';
import type { SelectProps } from 'antd';
import { formatStock } from '@app/shared';
import { useSearchCategoriesQuery } from '../api/categoryApi';
import type { CategoryDto } from '../model/types';

/** Уже выбранный товар: его может не быть в результатах поиска (товар в архиве или за пределами
 * первых 30 строк) — тогда поле показало бы технический идентификатор вместо названия. */
export interface CurrentCategory {
  id: string;
  name: string;
  isArchived?: boolean;
}

export interface CategorySearchSelectProps {
  value?: string;
  onChange: (id: string, category: CategoryDto) => void;
  /** Название выбранного товара — подставляется в список, если поиск его не вернул. */
  current?: CurrentCategory;
  /** Фильтр, а не строка документа: выбор можно снять. */
  allowClear?: boolean;
  onClear?: () => void;
  autoFocus?: boolean;
  disabled?: boolean;
  onKeyDown?: SelectProps['onKeyDown'];
}

/** §5: «в списке виден остаток» — категория + К/Р справа. */
export function CategorySearchSelect({ value, onChange, current, allowClear, onClear, autoFocus, disabled, onKeyDown }: CategorySearchSelectProps) {
  const [q, setQ] = useState('');
  const { data = [], isFetching } = useSearchCategoriesQuery({ q: q || undefined, limit: 30 });

  const options = useMemo(() => {
    const found = data.map((c) => ({
      value: c.id,
      disabled: false,
      label: (
        <span style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
          <span>{c.name}</span>
          {c.stock ? <span style={{ color: '#8c8c8c' }}>{formatStock(c.stock)}</span> : null}
        </span>
      ),
    }));
    if (!current || current.id !== value || data.some((c) => c.id === current.id)) return found;
    // Архивный товар выбрать заново нельзя — он остаётся в списке только как подпись значения.
    return [{ value: current.id, disabled: !!current.isArchived, label: <span>{categoryLabel(current)}</span> }, ...found];
  }, [data, current, value]);

  return (
    <Select
      value={value}
      showSearch
      allowClear={allowClear}
      onClear={onClear}
      filterOption={false}
      autoFocus={autoFocus}
      disabled={disabled}
      loading={isFetching}
      placeholder="Товар"
      style={{ width: '100%', minWidth: 200 }}
      onSearch={setQ}
      onKeyDown={onKeyDown}
      onChange={(id: string) => {
        const found = data.find((c) => c.id === id);
        if (found) onChange(id, found);
      }}
      options={options}
    />
  );
}

function categoryLabel(category: CurrentCategory): string {
  return category.isArchived ? `${category.name} (в архиве)` : category.name;
}
